import React from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import config from '../config';
import { useAuth } from '../auth/AuthContext';
import {
  ResizableColGroup,
  ResizableHeaderCell,
  TableEmptyRow,
  TableLoadingRow,
  useResizableColumns,
} from '../components/table/ResizableTable';

const TABLE_COLUMNS = [
  { key: 'store', label: 'Store', minWidth: 180, maxWidth: 520, defaultWidth: 260 },
  { key: 'sales_channel', label: 'Sales Channel', minWidth: 140, maxWidth: 360, defaultWidth: 180 },
  { key: 'external_id', label: 'External ID', minWidth: 130, maxWidth: 320, defaultWidth: 160 },
  { key: 'account_id', label: 'Account ID', minWidth: 180, maxWidth: 420, defaultWidth: 240 },
  { key: 'type', label: 'Type', minWidth: 110, maxWidth: 220, defaultWidth: 130 },
];

const PrintifyStoresPage = () => {
  const { fetchWithAuth } = useAuth();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const { getWidth, startColumnResize, tableMinWidth } = useResizableColumns(TABLE_COLUMNS, {
    storageKey: 'table.printify-stores',
  });

  const loadStores = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/shops`);
      if (!response.ok) {
        throw new Error('Failed to load Printify stores');
      }
      const payload = await response.json();
      setItems(Array.isArray(payload.items) ? payload.items : []);
    } catch (err) {
      setError(err.message || 'Failed to load stores');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadStores();
  }, [loadStores]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Printify Stores</Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadStores} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }} stickyHeader>
            <ResizableColGroup columns={TABLE_COLUMNS} getWidth={getWidth} />
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((column) => (
                  <ResizableHeaderCell
                    key={column.key}
                    column={column}
                    getWidth={getWidth}
                    onResizeStart={startColumnResize}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingRow colSpan={TABLE_COLUMNS.length} label="Loading stores..." />
              ) : items.length === 0 ? (
                <TableEmptyRow colSpan={TABLE_COLUMNS.length} label="No Printify stores found." />
              ) : (
                items.map((store) => (
                  <TableRow key={store.id} hover>
                    <TableCell>{store.title || '—'}</TableCell>
                    <TableCell>{store.sales_channel || '—'}</TableCell>
                    <TableCell>{store.external_id ?? '—'}</TableCell>
                    <TableCell>{store.account_id || '—'}</TableCell>
                    <TableCell>{store.is_template ? 'Template' : 'Connected'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default PrintifyStoresPage;
